// Shared helpers for the POS gift voucher screens.

// Gift Voucher mode accent (pink) — sits alongside Return (orange) and Exchange (indigo).
export const GV = { main: '#DB2777', dark: '#BE185D', text: '#9D174D', bg: '#FDF2F8', border: '#FBCFE8' };

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export const inr = (v, d = 0) => `₹${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: d, maximumFractionDigits: d })}`;
export const inr2 = (v) => inr(v, 2);
export const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

export const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
export const fmtDateTime = (iso) => (iso
  ? new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: 'numeric', minute: '2-digit', hour12: true })
  : '—');

// 10-digit Indian mobile; +91 / 91 / 0 prefixes and spaces are allowed.
export const normPhone = (p) => {
  let d = String(p || '').replace(/\D/g, '');
  if (d.length === 12 && d.startsWith('91')) d = d.slice(2);
  if (d.length === 11 && d.startsWith('0')) d = d.slice(1);
  return d;
};

export const validityLabel = (t) => {
  if (!t) return '';
  if (t.validity_type === 'days_from_issue') return `Valid ${t.validity_days} days from issue`;
  if (t.validity_type === 'fixed_date') return `Valid till ${fmtDate(t.fixed_expiry_date)}`;
  return 'No expiry';
};
export const pinLabel = (t) => (Number(t?.pin_required_above || 0) === 0 ? 'PIN always required' : `PIN above ${inr(t.pin_required_above)}`);

export const typeAllowsValue = (t, value) => {
  const v = Number(value);
  if (!t || !(v > 0)) return false;
  if ((t.denominations || []).map(Number).includes(v)) return true;
  if (!t.allow_custom_amount) return false;
  if (t.min_amount != null && v < Number(t.min_amount)) return false;
  if (t.max_amount != null && v > Number(t.max_amount)) return false;
  return /^\d+(\.\d{1,2})?$/.test(String(value));
};

export const recipientsComplete = (line) => line.recipients.length === Number(line.qty)
  && line.recipients.every((r) => r.name?.trim() && EMAIL_RE.test(r.email?.trim() || '') && (!r.phone || normPhone(r.phone).length === 10));

// Local (pre-save) totals so the summary updates as the cashier types.
export function localTotals(sale) {
  const face = sale.lines.reduce((a, l) => a + (Number(l.value) || 0) * (Number(l.qty) || 0), 0);
  const d = sale.discount;
  let disc = 0;
  if (d && Number(d.value) > 0) disc = d.type === 'percentage' ? round2(face * Number(d.value) / 100) : Math.min(face, Number(d.value));
  const count = sale.lines.reduce((a, l) => a + (Number(l.qty) || 0), 0);
  return { face, disc, payable: round2(face - disc), count };
}

// Draft payload for POST/PATCH /gift_voucher_sales. Recipients are only sent
// when a line's list is complete (the API rejects a count ≠ quantity).
export function draftPayload(sale) {
  return {
    gift_voucher_sale: {
      buyer_customer_id: sale.buyer?.id,
      buyer_email: sale.buyerEmail.trim(),
      delivery_mode: sale.delivery,
      discount_type: sale.discount && Number(sale.discount.value) > 0 ? sale.discount.type : null,
      discount_value: sale.discount && Number(sale.discount.value) > 0 ? Number(sale.discount.value) : null,
      discount_reason: sale.discount?.reason || null,
      lines: sale.lines.map((l) => ({
        gift_voucher_type_id: l.typeId,
        face_value: Number(l.value),
        quantity: Number(l.qty),
        ...(recipientsComplete(l)
          ? { recipients: l.recipients.map((r) => ({ name: r.name.trim(), email: r.email.trim(), phone: r.phone?.trim() || '' })) }
          : {}),
      })),
    },
  };
}

export const APPROVAL_LABEL = {
  voucher_sale_discount: 'Discount',
  voucher_sale_quantity: 'Quantity above the type limit',
};

export const SALE_STATUS = {
  paid: { label: 'Paid', bg: '#F0FDF4', color: '#16A34A' },
  awaiting_issue: { label: 'Awaiting issue', bg: '#FFFBEB', color: '#B45309' },
  rejected: { label: 'Rejected', bg: '#FEF2F2', color: '#DC2626' },
  partially_cancelled: { label: 'Part. cancelled', bg: '#FFFBEB', color: '#B45309' },
  cancelled: { label: 'Cancelled', bg: '#FEF2F2', color: '#DC2626' },
  draft: { label: 'Draft', bg: '#F1F5F9', color: '#64748B' },
};
export const saleStatusKey = (s) => (s?.status === 'awaiting_issue' && s?.issue_rejected_at ? 'rejected' : s?.status);

export const VOUCHER_STATUS = {
  active: { label: 'Active', bg: '#F0FDF4', color: '#16A34A' },
  redeemed: { label: 'Redeemed', bg: '#F1F5F9', color: '#64748B' },
  expired: { label: 'Expired', bg: '#FFFBEB', color: '#B45309' },
  blocked: { label: 'Blocked', bg: '#FEF2F2', color: '#DC2626' },
  pin_locked: { label: 'PIN locked', bg: '#FEF2F2', color: '#DC2626' },
  cancelled: { label: 'Cancelled', bg: '#FEF2F2', color: '#DC2626' },
  revoked: { label: 'Revoked', bg: '#F1F5F9', color: '#64748B' },
};

export const DELIVERY_STATUS = {
  queued: { label: 'Queued', bg: '#F1F5F9', color: '#64748B' },
  sent: { label: 'Sent', bg: '#F0FDF4', color: '#16A34A' },
  failed: { label: 'Failed', bg: '#FEF2F2', color: '#DC2626' },
};

export const DELIVERY_KIND = {
  issue: 'All codes + PINs (PDF + CSV)', issue_summary: 'Summary for buyer (no PINs)', resend: 'New PINs', reminder: 'Expiry reminder', cancellation: 'Cancellation note',
};

export const isGiftVoucherMode = (mode = {}) => String(mode.code || '').toUpperCase() === 'GIFT_VOUCHER'
  || `${mode.name || ''}`.toLowerCase().includes('gift voucher');
// Mirrors GiftVouchers::SaleService — these modes can't buy vouchers / need a reference.
export const isBlockedForVoucherSale = (mode = {}) => ['GIFT_VOUCHER', 'STORE_CREDIT'].includes(String(mode.code || '').toUpperCase())
  || isGiftVoucherMode(mode) || /store credit/i.test(mode.name || '');
export const isCashModeGv = (mode = {}) => String(mode.code || '').toUpperCase() === 'CASH' || /cash/i.test(mode.name || '');
export const needsReference = (mode = {}) => /BANK|NEFT|RTGS|IMPS|TRANSFER|CHEQUE|CHECK/.test(String(mode.code || '').toUpperCase());
